import StudentDashboardLayout from "@/components/Student/DashboardLayout";
import CourseWidgets from "@/components/Instructor/Dashboard-Section/widgets/CourseWidget";
import Link from "next/link";
import { useState } from "react";
import { encodeForURL } from "@/context/utilities";
import DataDisplay from "@/components/Common/DataFetcher";
import axios from "axios";
import { useAuthContext } from "@/context/auth/AuthContext";
import { components } from "react-select";
import { connectToDatabase } from "@/components/Server/Database/MongoDB";
import { useRouter } from "next/router";
import researchIcon from "../../../public/images/icons/research-cv.webp";

const StudentCourse = ({ info }) => {
  const router = useRouter();
  const [data, setData] = useState([]);
  const [counselling, setCounselling] = useState([]);
  const [MCQ, setMCQ] = useState([]);
  const [research, setResearch] = useState([]);
  const { user } = useAuthContext();

  const allowedSubjectIds =
    user?.batch?.subject?.map((subject) => subject._id) || [];

  const fetchSubjects = async (
    currentPage,
    limit,
    search,
    searchBy,
    setPages,
    setError,
    setLoading
  ) => {
    if (user)
      try {
        setLoading(true);
        const response = await axios.get(
          `/api/video/subjects/?userId=${
            user.email
          }&page=${currentPage}&limit=${limit}&search=${encodeForURL(
            search
          )}&searchBy=${searchBy}`
        );

        if (response.status === 200) {
          setData(response.data.data);
          setPages(response.data.pages);
          setError(null);
        } else {
          setError(
            response.data.message || "An error occurred during data fetching."
          );
        }
      } catch (err) {
        setError("Fetching data failed. Please try again later.");
      } finally {
        setLoading(false);
      }
  };

  const fetchCounselling = async (
    currentPage,
    limit,
    search,
    searchBy,
    setPages,
    setError,
    setLoading
  ) => {
    if (user)
      try {
        setLoading(true);
        const response = await axios.get(
          `/api/data?collection=counselling&userId=${
            user.email
          }&page=${currentPage}&limit=${limit}&search=${encodeForURL(
            search
          )}&searchBy=${searchBy}`
        );

        if (response.status === 200) {
          setCounselling(response.data.data);
          setPages(response.data.pages);
          setError(null);
        } else {
          setError(
            response.data.message || "An error occurred during data fetching."
          );
        }
      } catch (err) {
        setError("Fetching data failed. Please try again later.");
      } finally {
        setLoading(false);
      }
  };

  const fetchMCQReviews = async (
    currentPage,
    limit,
    search,
    searchBy,
    setPages,
    setError,
    setLoading
  ) => {
    if (user)
      try {
        setLoading(true);
        const response = await axios.get(
          `/api/data?collection=MCQVideos&userId=${
            user.email
          }&page=${currentPage}&limit=${limit}&search=${encodeForURL(
            search
          )}&searchBy=${searchBy}`
        );

        if (response.status === 200) {
          setMCQ(response.data.data);
          setPages(response.data.pages);
          setError(null);
        } else {
          setError(
            response.data.message || "An error occurred during data fetching."
          );
        }
      } catch (err) {
        setError("Fetching data failed. Please try again later.");
      } finally {
        setLoading(false);
      }
  };

  const fetchResearch = async (
    currentPage,
    limit,
    search,
    searchBy,
    setPages,
    setError,
    setLoading
  ) => {
    if (user)
      try {
        setLoading(true);
        const response = await axios.get(
          `/api/data?collection=research&userId=${
            user.email
          }&page=${currentPage}&limit=${limit}&search=${encodeForURL(
            search
          )}&searchBy=${searchBy}`
        );

        if (response.status === 200) {
          setResearch(response.data.data);
          setPages(response.data.pages);
          setError(null);
        } else {
          setError(
            response.data.message || "An error occurred during data fetching."
          );
        }
      } catch (err) {
        setError("Fetching data failed. Please try again later.");
      } finally {
        setLoading(false);
      }
  };

  const tabs = [
    {
      id: "academic-courses",
      selected:
        (router.query.tab === "academic-courses" || !router.query.tab) && true,
      displayName: "Academic Courses",
      component: (
        <DataDisplay user={user} fetchData={fetchSubjects} setData={setData}>
          {data?.map((slide, index) => {
            const isInBatch = allowedSubjectIds.includes(slide._id);

            return (
              <div
                className="col-md-4 col-12"
                key={`course-enrolled-${index}`}
              >
                <CourseWidgets
                  data={slide}
                  href={
                    isInBatch
                      ? `/student/library/${encodeForURL(slide.displayName)}-${
                          slide._id
                        }`
                      : "#"
                  }
                  courseStyle="two"
                  isProgress={true}
                  isCompleted={false}
                  isEdit={false}
                  image={true}
                  showDescription={false}
                  showAuthor={false}
                  button={isInBatch ? "Start Course" : "Locked"}
                  locked={!isInBatch}
                  tooltip="This subject is not part of your batch"
                />
              </div>
            );
          })}
        </DataDisplay>
      ),
    },
    {
      id: "research",
      displayName: "Research & CV",
      selected: router.query.tab === "research",
      component: (
        <DataDisplay
          user={user}
          empty={research.length === 0}
          config={{
            empty: {
              image: {
                src: researchIcon,
                width: 100,
                height: 100,
              },
              title: "Nothing to Show",
              subtitle:
                "Currently there is nothing available for research and CV",
            },
          }}
          fetchData={fetchResearch}
          setData={setResearch}
        >
          {research?.map((slide, index) => (
            <div className="col-md-4 col-12" key={`research-${index}`}>
              <CourseWidgets
                data={slide}
                href={`/student/library/watch/research-${slide._id}`}
                courseStyle="two"
                isProgress={true}
                isCompleted={false}
                isEdit={false}
                showDescription={false}
                showAuthor={false}
                button="Start Course"
              />
            </div>
          ))}
        </DataDisplay>
      ),
    },
    {
      id: "counselling",
      displayName: "Counselling",
      selected: router.query.tab === "counselling",
      component: (
        <DataDisplay
          user={user}
          fetchData={fetchCounselling}
          setData={setCounselling}
        >
          {counselling?.map((slide, index) => (
            <div className="col-md-4 col-12" key={`counselling-${index}`}>
              <CourseWidgets
                data={slide}
                href={`/student/library/watch/counselling-${slide._id}`}
                courseStyle="two"
                isProgress={false}
                isCompleted={false}
                isEdit={false}
                showDescription={false}
                showAuthor={false}
                button="Watch Video"
              />
            </div>
          ))}
        </DataDisplay>
      ),
    },
    {
      id: "mcq-review",
      displayName: "MCQ Review",
      selected: router.query.tab === "mcq-review",
      component: (
        <DataDisplay user={user} fetchData={fetchMCQReviews} setData={setMCQ}>
          {MCQ?.map((slide, index) => (
            <div className="col-md-4 col-12" key={`mcq-${index}`}>
              <CourseWidgets
                data={slide}
                href={`/student/library/watch/MCQVideos-${slide._id}`}
                courseStyle="two"
                isProgress={false}
                isCompleted={false}
                isEdit={false}
                showDescription={false}
                showAuthor={false}
                button="Watch Video"
              />
            </div>
          ))}
        </DataDisplay>
      ),
    },
  ];
  return (
    <StudentDashboardLayout
      brand={JSON.parse(info)}
      sidebar={true}
      title="Student Enrolled Course - Online Courses & Education Powered By THEWEBVALE"
    >
      <div className="rbt-dashboard-content bg-color-white rbt-shadow-box">
        <div className="content">
          <div className="section-title">
            <h4 className="rbt-title-style-3">Subjects</h4>
          </div>
          <div className="advance-tab-button mb--30">
            <ul
              className="nav nav-tabs tab-button-style-2 justify-content-start"
              id="myTab-4"
              role="tablist"
            >
              {tabs.map((tab, index) => {
                return (
                  <li key={tab.id} role="presentation">
                    <Link
                      href="#"
                      className={`tab-button ${tab.selected ? "active" : ""}`}
                      id={tab.id}
                      data-bs-toggle="tab"
                      data-bs-target={`#${tab.id}-${index}`}
                      role="tab"
                      aria-controls={`${tab.id}-${index}`}
                      aria-selected={tab.selected}
                    >
                      <span className="title">{tab.displayName}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="tab-content">
            {tabs.map((tab, index) => {
              return (
                <div
                  key={`${tab.id}-${index}`}
                  className={`tab-pane fade ${
                    tab.selected ? "active show" : ""
                  }`}
                  id={`${tab.id}-${index}`}
                  role="tabpanel"
                  aria-labelledby={tab.id}
                >
                  {tab.component}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </StudentDashboardLayout>
  );
};

export const getServerSideProps = async (context) => {
  const domainName = context.req ? context.req.headers.host : null;

  const { db } = await connectToDatabase();

  const info = await db.collection("partners").findOne({
    key: process.env.DEFAULT || domainName,
  });

  if (!info) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      info: JSON.stringify(info),
    },
  };
};

export default StudentCourse;
